import { Capacitor, registerPlugin } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

/** Local native plugin (ios/App SceneDelegate.swift): paints the band behind
 *  the iOS status bar to match the web app's actual surface, and reports the
 *  real status-bar height (the webview reports env()=0 despite the bar). */
interface HTSChromePlugin {
  setBandColor(options: { hex: string }): Promise<void>;
  getSafeTop(): Promise<{ top: number }>;
}
const HTSChrome = registerPlugin<HTSChromePlugin>('HTSChrome');

/**
 * Keep the iOS status bar legible and color-matched — the native-shell twin
 * of the theme-color meta sync for mobile browser chrome, shared with the
 * Project Portal app (same SceneDelegate/HTSChromePlugin pair). No-op
 * outside the Capacitor app (browsers own their chrome there).
 *
 * Every surface under this bar is dark — the navy login page and the
 * --hts-shell-bg header (#0D2152 light theme, #0E1422 dark) — so the clock
 * text is ALWAYS white and only the band color varies.
 */
export function syncNativeChrome(resolvedTheme: 'light' | 'dark', signedIn: boolean): void {
  if (!Capacitor.isNativePlatform()) return;
  void HTSChrome.getSafeTop()
    .then(({ top }) => {
      // The channel carries HALF the status-bar height (device-test on the
      // portal app: full inset + breathing was 'too much, cut it in 1/2');
      // the Layout header consumes it via max(env(...), var(--hts-safe-top)).
      // Browsers never set this var — their inset stays env() (0 on desktop).
      if (top > 0) document.documentElement.style.setProperty('--hts-safe-top', `${Math.round(top / 2)}px`);
    })
    .catch(() => undefined);
  void StatusBar.setStyle({ style: Style.Light }).catch(() => undefined);
  const bandHex = resolvedTheme === 'dark' && signedIn ? '#0E1422' : '#0D2152';
  void HTSChrome.setBandColor({ hex: bandHex }).catch(() => undefined);
}
